import React, { useEffect, useRef } from 'react';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';
// 2-finger panning plugin (prevents the mobile scroll-trap), same as DynamicMapboxMap.
import MultiTouch from 'mapbox-gl-multitouch';

import { FormattedMessage } from '../../../../util/reactIntl';
import { useConfiguration } from '../../../../context/configurationContext';
import { loadMapboxGl } from '../../../../util/loadMapbox';
import { landingSectionSurfaceClassName } from '../../../LandingPage/landingSectionSurface';

import SectionContainer from '../SectionContainer';
import css from './SectionZurichMap.module.css';

// Real Zürich coworking / flexible-office spaces on cowork24 (coordinates + profile ids
// from the live Marketplace API). Each marker links to the provider's public profile.
const ZURICH_SPACES = [
  {
    id: 'impact-hub-zuerich',
    name: 'Impact Hub Zürich',
    lat: 47.3723,
    lng: 8.53209,
    profileId: '690c4e4c-a5d3-4334-8b10-684e09754a44',
    logo:
      'https://sharetribe.imgix.net/68a32a0c-65b6-47ae-97b1-43643e03bc76/691d9c3c-0e4b-451b-a9e5-e1dd4081b66f?auto=format&crop=edges&fit=crop&h=480&w=480&s=7541ef75f5a032d6273564db2dbaed75',
  },
  {
    id: 'headsquarter',
    name: 'Headsquarter',
    lat: 47.36684,
    lng: 8.53496,
    profileId: '68c7c453-28a4-43b1-8e2b-95f4cd3b0cd4',
    logo:
      'https://sharetribe.imgix.net/68a32a0c-65b6-47ae-97b1-43643e03bc76/6a037037-cecd-4a9f-9b6b-5180998c206d?auto=format&crop=edges&fit=crop&h=480&w=480&s=6ef65e799b209d2133d30ac596e8aa61',
  },
  {
    id: 'satellite-office-gryffenberg',
    name: 'Satellite Office GmbH - Gryffenberg',
    lat: 47.36815,
    lng: 8.54004,
    profileId: '6a185b63-4823-442d-8328-6bbff12f7aa3',
    logo:
      'https://sharetribe.imgix.net/68a32a0c-65b6-47ae-97b1-43643e03bc76/6a185b89-65f7-4b13-8f3f-5c3d7f633b39?auto=format&crop=edges&fit=crop&h=480&w=480&s=6e473ee4decca41faecd7bff53813335',
  },
  {
    id: 'coworking-seefeld',
    name: 'Coworking Seefeld',
    lat: 47.3632,
    lng: 8.55182,
    profileId: '68de5470-361b-4e6a-b89d-abb71eb98c08',
    logo:
      'https://sharetribe.imgix.net/68a32a0c-65b6-47ae-97b1-43643e03bc76/698f3d90-c887-4a6b-9d73-bb38f62dc864?auto=format&crop=edges&fit=crop&h=480&w=480&s=a63097bc98b93eb2a0a83f1377ad4c2f',
  },
  {
    id: 'greencityoffice',
    name: 'GreencityOffice',
    lat: 47.34227,
    lng: 8.52024,
    profileId: '6a156dab-d2cf-4036-875b-d5a15a9d5396',
    logo:
      'https://sharetribe.imgix.net/68a32a0c-65b6-47ae-97b1-43643e03bc76/6a156dfe-22b9-434e-a2aa-ea462e530426?auto=format&crop=edges&fit=crop&h=480&w=480&s=571229144a9fc79c4c63e5a828ce1f22',
  },
  {
    id: 'flexoffice-schweiz',
    name: 'FlexOffice (Schweiz) AG',
    lat: 47.39918,
    lng: 8.45383,
    profileId: '68e36665-2127-41f3-8d12-e070ca3ff11d',
    logo:
      'https://sharetribe.imgix.net/68a32a0c-65b6-47ae-97b1-43643e03bc76/690367ae-f58e-43b0-b823-bea8bad6559e?auto=format&crop=edges&fit=crop&h=480&w=480&s=43282722dc8fe05b5b40d61bd3a61b50',
  },
  {
    id: 'fabrik11',
    name: 'FABRIK11',
    lat: 47.39773,
    lng: 8.60407,
    profileId: '69e91588-ebd7-4607-8cf8-da78a1d6ad3d',
    logo:
      'https://sharetribe.imgix.net/68a32a0c-65b6-47ae-97b1-43643e03bc76/69e915a8-935d-4ad2-9b3b-5a736dddb2fb?auto=format&crop=edges&fit=crop&h=480&w=480&s=ee1dcd5ff8bde4be26f52edff74f8bfc',
  },
];

const ZURICH_CENTER = { lat: 47.3769, lng: 8.5417 };

/**
 * Interactive Zürich map for the Space Owner landing page: shows the real coworking
 * spaces already listing on cowork24, each marker a rounded avatar that links to the
 * provider's public profile (`/u/:id`).
 *
 * SSR-safe: the component renders only a sized container on the server / first paint and
 * initialises Mapbox GL after mount, once the section nears the viewport: the library is loaded
 * on demand (util/loadMapbox.js).
 *
 * @component
 * @param {Object} props
 * @param {string} props.sectionId
 * @param {string?} props.className
 * @param {string?} props.rootClassName
 * @param {Object?} props.appearance
 * @param {Object?} props.options
 * @param {('white'|'muted')?} props.landingSurface
 * @param {string?} props.eyebrowId
 * @param {string?} props.titleId
 * @param {string?} props.subtitleId
 * @returns {JSX.Element}
 */
const SectionZurichMap = props => {
  const {
    sectionId,
    className,
    rootClassName,
    appearance,
    options,
    landingSurface,
    eyebrowId,
    titleId,
    subtitleId,
  } = props;

  const config = useConfiguration();
  const mapboxAccessToken = config?.maps?.mapboxAccessToken;
  const history = useHistory();
  const historyRef = useRef(history);
  historyRef.current = history;

  const containerRef = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    const token = mapboxAccessToken;
    let cancelled = false;
    let observer = null;

    const init = () => {
      const mapboxgl = typeof window !== 'undefined' ? window.mapboxgl : null;
      if (!mapboxgl || !containerRef.current || mapRef.current) {
        return !!mapRef.current;
      }
      if (token && !mapboxgl.accessToken) {
        mapboxgl.accessToken = token;
      }
      if (!mapboxgl.accessToken) {
        return false;
      }

      const map = new mapboxgl.Map({
        container: containerRef.current,
        style: 'mapbox://styles/mapbox/light-v11',
        center: [ZURICH_CENTER.lng, ZURICH_CENTER.lat],
        zoom: 11.5,
        scrollZoom: false,
        dragRotate: false,
        attributionControl: true,
      });
      map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');
      map.addControl(new MultiTouch());
      mapRef.current = map;

      const bounds = new mapboxgl.LngLatBounds();
      ZURICH_SPACES.forEach(space => {
        const path = `/u/${space.profileId}`;
        const el = document.createElement('a');
        el.className = css.marker;
        el.href = path;
        el.title = space.name;
        el.setAttribute('aria-label', space.name);

        const img = document.createElement('img');
        img.src = space.logo;
        img.alt = space.name;
        img.loading = 'lazy';
        img.className = css.markerImg;
        el.appendChild(img);

        el.addEventListener('click', e => {
          // Let the browser handle modified / non-primary clicks (cmd/ctrl/shift/alt =
          // open in a new tab/window) via the real href — mirrors react-router's Link.
          if (
            e.defaultPrevented ||
            e.button !== 0 ||
            e.metaKey ||
            e.ctrlKey ||
            e.shiftKey ||
            e.altKey
          ) {
            return;
          }
          // SPA navigation when available; the real href is the no-JS fallback.
          if (historyRef.current && typeof historyRef.current.push === 'function') {
            e.preventDefault();
            historyRef.current.push(path);
          }
        });

        new mapboxgl.Marker({ element: el }).setLngLat([space.lng, space.lat]).addTo(map);
        // Mapbox GL stamps role="img" onto custom marker elements, which is an
        // invalid ARIA role for a link (axe: aria-allowed-role). The anchor's
        // implicit link role + aria-label is the correct semantics.
        el.removeAttribute('role');
        bounds.extend([space.lng, space.lat]);
      });

      if (!bounds.isEmpty()) {
        map.fitBounds(bounds, { padding: 64, maxZoom: 13, duration: 0 });
      }
      return true;
    };

    // Mapbox GL JS is loaded on demand (util/loadMapbox.js), once the section approaches the
    // viewport — it sits below the fold. On failure the container stays as an empty state.
    const loadAndInit = () => {
      loadMapboxGl()
        .then(() => {
          if (!cancelled) {
            init();
          }
        })
        .catch(() => {});
    };

    const el = containerRef.current;
    if (el && typeof window.IntersectionObserver === 'function') {
      observer = new window.IntersectionObserver(
        entries => {
          if (entries.some(entry => entry.isIntersecting)) {
            observer.disconnect();
            loadAndInit();
          }
        },
        { rootMargin: '600px 0px' }
      );
      observer.observe(el);
    } else {
      loadAndInit();
    }

    return () => {
      cancelled = true;
      if (observer) {
        observer.disconnect();
      }
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [mapboxAccessToken]);

  return (
    <SectionContainer
      id={sectionId}
      className={className}
      rootClassName={classNames(rootClassName, landingSectionSurfaceClassName(landingSurface))}
      appearance={appearance}
      options={options}
    >
      <div className={css.wrapper}>
        {titleId ? (
          <div className={css.heading}>
            {eyebrowId ? (
              <span className={css.eyebrow}>
                <FormattedMessage id={eyebrowId} />
              </span>
            ) : null}
            <h2 className={css.title}>
              <FormattedMessage id={titleId} />
            </h2>
            {subtitleId ? (
              <p className={css.subtitle}>
                <FormattedMessage id={subtitleId} />
              </p>
            ) : null}
          </div>
        ) : null}
        <div className={css.mapCard}>
          <div
            ref={containerRef}
            className={css.map}
            role="application"
            aria-label="Coworking Spaces in Zürich"
          />
        </div>
      </div>
    </SectionContainer>
  );
};

export default SectionZurichMap;
